import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import RunBulkOpButton from './run-bulk-op-button';
import RunningBulkOpButton from './running-bulk-op-button';
import { BulkImportModel } from '../../../../types';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../state/zustand-store';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  getAllBulkImports,
  getBulkImportTypes,
} from '../../../api/users-api';
import BulkOpsGridToolbar from './bulk-ops-grid-toolbar';
import AvatarChip from '../../../shared/avatar-chip';
import DeleteBulkOpsDialog from './delete-bulk-ops-dialog';
import RefreshBulkOpsDialog from './refresh-bulk-ops-dialog';
import RunBulkOpsDialog from './run-bulk-ops-dialog';
import RunConfirmBulkOpDialog from './run-confirm-bulk-op-dialog';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import useFirebaseMessageHandler from '../../../shared/firebase/useFirebaseMessageHandler';
import { useQueryClient } from '@tanstack/react-query';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import MoreVert from '@mui/icons-material/MoreVert';
import Pageview from '@mui/icons-material/Pageview';
import PlaylistRemove from '@mui/icons-material/PlaylistRemove';
import RestorePage from '@mui/icons-material/RestorePage';

interface BulkOpsGridProps {
  loggedInUserEmail: string;
  toolsRoles: string[];
}

function OverflowMenu({
  row,
  usesSpreadsheet,
  canRefresh,
  refreshDisabledReason,
  onViewSpreadsheet,
  onRefresh,
}: {
  row: BulkImportModel;
  usesSpreadsheet: boolean;
  canRefresh: boolean;
  refreshDisabledReason?: string;
  onViewSpreadsheet: () => void;
  onRefresh: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!usesSpreadsheet) return <Box sx={{ width: 30 }} />;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onViewSpreadsheet();
          }}
        >
          <ListItemIcon>
            <Pageview fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Spreadsheet</ListItemText>
        </MenuItem>
        {row.path === 'guideMe' && (
          <Tooltip arrow title={refreshDisabledReason ?? ''} placement="left">
            <span>
              <MenuItem
                disabled={!canRefresh}
                onClick={() => {
                  setAnchorEl(null);
                  onRefresh();
                }}
              >
                <ListItemIcon>
                  <RestorePage fontSize="small" />
                </ListItemIcon>
                <ListItemText>Refresh Spreadsheet Data</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>
    </>
  );
}

export default function BulkOpsGrid({
  loggedInUserEmail,
  toolsRoles,
}: BulkOpsGridProps) {
  const { firebaseUser } = useStore();
  const { handleViewSpreadsheet, changeLogRoles } = useBulkOpActions();
  const [selectedImportModel, setSelectedImportModel] =
    useState<BulkImportModel | null>(null);
  const [openDeleteBulkOperations, setOpenDeleteBulkOperations] =
    useState(false);
  const [openRefreshBulkOps, setOpenRefreshBulkOps] = useState(false);
  const [openRunBulkOperation, setOpenRunBulkOperation] = useState(false);
  const [openRunConfirmDialog, setOpenRunConfirmDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toolsRoleMap = useMemo(() => {
    const map = new Map<string, (typeof ToolsRoles)[keyof typeof ToolsRoles]>();
    Object.values(ToolsRoles).forEach((role) => {
      map.set(role.value, role);
    });
    return map;
  }, []);

  const {
    data: bulkImportModels,
    isLoading: modelsLoading,
    isFetching: modelsFetching,
  } = useQuery({
    queryKey: [UserKeys.BulkOps],
    queryFn: () => getAllBulkImports(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('bulk import data'),
    },
  });

  const {
    data: bulkImportTypes,
    isLoading: typesLoading,
    isFetching: typesFetching,
  } = useQuery({
    queryKey: [UserKeys.BulkOpsTypes],
    queryFn: () => getBulkImportTypes(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('bulk import data'),
    },
  });

  const firebaseCallback = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [UserKeys.BulkOps] });
  }, [queryClient]);

  useFirebaseMessageHandler(
    ['bulkOpFinished', 'exportFinished'],
    firebaseUser.firebaseDbPath,
    firebaseCallback
  );

  const columns: GridColDef<BulkImportModel>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        editable: false,
        sortable: true,
        filterable: true,
        flex: 1,
        renderCell: (params) => {
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'name',
        headerName: 'Operation Name',
        minWidth: 170,
        flex: 1,
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'bulkImport',
        headerName: 'Operation Type',
        width: 170,
        type: 'singleSelect',
        valueOptions: bulkImportTypes?.map((bulkImport) => {
          return {
            label: bulkImport.niceName,
            value: bulkImport.name,
          };
        }),
        valueGetter: (value: string | null) => {
          if (!value) return null;
          const bulkOp = bulkImportTypes?.find(
            (bulkOp) => bulkOp.name === value
          );
          return bulkOp?.name;
        },
      },
      {
        field: 'path',
        headerName: 'Creation Type',
        width: 170,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Guided', value: 'guideMe' },
          { label: 'Advanced', value: 'advanced' },
        ],
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'lastRan',
        headerName: 'Last Ran On',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number | null) => (value ? new Date(value) : null),
        renderCell: (params) =>
          params.value ? params.value.toLocaleString() : 'Never Ran',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: (params: GridRenderCellParams<BulkImportModel>) => {
          const bulkImportType = bulkImportTypes?.find(
            (type) => type.name === params.row.bulkImport
          );
          const rolePermission = bulkImportType?.role
            ? toolsRoleMap.get(bulkImportType.role)
            : undefined;
          const permissionGranted = isAbleToSeeUIElement(
            toolsRoles,
            bulkImportType?.role || ''
          );
          const isThisOpRunning = params.row.status === 'RUNNING';

          const disabledReason = !permissionGranted
            ? Messages.Errors.Permission.Acting(rolePermission?.name || '')
            : undefined;

          const runButton = isThisOpRunning ? (
            <Tooltip arrow title="This bulk op is currently running.">
              <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                <RunningBulkOpButton onClick={(e) => e.stopPropagation()} />
              </span>
            </Tooltip>
          ) : (
            <Tooltip arrow title={disabledReason ?? ''}>
              <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                <RunBulkOpButton
                  disabled={!!disabledReason}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImportModel(params.row);
                    if (bulkImportType?.usesSpreadsheet) {
                      setOpenRunBulkOperation(true);
                    } else {
                      setOpenRunConfirmDialog(true);
                    }
                  }}
                />
              </span>
            </Tooltip>
          );

          const canRefresh = isAbleToSeeUIElement(
            toolsRoles,
            bulkImportType?.role || ''
          );

          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={0.5}
              sx={{ width: '100%' }}
            >
              {runButton}
              <OverflowMenu
                row={params.row}
                usesSpreadsheet={bulkImportType?.usesSpreadsheet ?? false}
                canRefresh={canRefresh}
                refreshDisabledReason={
                  canRefresh
                    ? undefined
                    : Messages.Errors.Permission.Acting(
                        rolePermission?.name || ''
                      )
                }
                onViewSpreadsheet={() =>
                  handleViewSpreadsheet(params.row, loggedInUserEmail)
                }
                onRefresh={() => {
                  setSelectedImportModel(params.row);
                  setOpenRefreshBulkOps(true);
                }}
              />
            </Stack>
          );
        },
      },
    ],
    [
      bulkImportTypes,
      toolsRoles,
      toolsRoleMap,
      loggedInUserEmail,
      handleViewSpreadsheet,
    ]
  );

  const selectedBulkOps = (bulkImportModels ?? []).filter((m) =>
    selectedRows.includes(m.id)
  );

  const { viewType } = useUserSettings();

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      setOpenDeleteBulkOperations,
      selectedBulkOps,
    }),
    [selectedBulkOps, toolsRoles]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(BulkOpsGridToolbar, toolbarProps),
          ...createGridOverlays('Bulk Imports', <PlaylistRemove />),
          baseMenuItem: DisabledTooltipMenuItem,
        }}
        defaultGridState={defaultGridState}
        gridKey={viewType.bulkOpsGridStates?.activeGridState}
        rows={bulkImportModels || []}
        columns={columns}
        loading={
          modelsLoading || modelsFetching || typesLoading || typesFetching
        }
        keepNonExistentRowsSelected
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={(row) => {
          setSearchParams((prev) => {
            prev.set('selectedBulkOp', row.row.id.toString());
            return prev;
          });
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        gridStateKey="bulkOpsGridStates"
      />
      {selectedBulkOps.length > 0 && openDeleteBulkOperations ? (
        <DeleteBulkOpsDialog
          open={openDeleteBulkOperations}
          handleClose={() => setOpenDeleteBulkOperations(false)}
          selectedBulkOps={selectedBulkOps}
        />
      ) : null}
      {selectedImportModel && openRefreshBulkOps ? (
        <RefreshBulkOpsDialog
          open={openRefreshBulkOps}
          handleClose={() => setOpenRefreshBulkOps(false)}
          selectedBulkOp={selectedImportModel}
        />
      ) : null}
      {selectedImportModel && openRunBulkOperation ? (
        <RunBulkOpsDialog
          open={openRunBulkOperation}
          handleClose={() => setOpenRunBulkOperation(false)}
          selectedBulkOp={selectedImportModel}
          loggedInUserEmail={loggedInUserEmail}
          bulkImportTypes={bulkImportTypes || []}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedImportModel && openRunConfirmDialog ? (
        <RunConfirmBulkOpDialog
          open={openRunConfirmDialog}
          handleClose={() => setOpenRunConfirmDialog(false)}
          selectedBulkOp={selectedImportModel}
          bulkImportRole={
            bulkImportTypes?.find(
              (t) => t.name === selectedImportModel.bulkImport
            )?.role
          }
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
